import * as React from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { useQueryClient } from 'react-query';

import { startFeatureTrial } from 'src/api/functions/subscriptionApi';
import { isErrorResponseResult, TPlanUpgradeRequired } from 'src/api/utils/errorHandlers';
import {
  ENTERPRISE_CONTACT_URL,
  isPlanHidden,
} from 'src/admin-pages/Subscription/planVisibility';
import { useAccessToken } from 'src/hooks/useAccessToken';

import styles from './FeatureUnavailableBanner.module.scss';

const messages = defineMessages({
  upgradeTo: {
    id: 'planUpgradeBanner.upgradeTo',
    defaultMessage: 'Upgrade to {plan} to access this feature.',
  },
  contactSales: {
    id: 'planUpgradeBanner.contactSales',
    defaultMessage: 'Contact sales for an Enterprise plan to access this feature.',
  },
  viewPlans: {
    id: 'planUpgradeBanner.viewPlans',
    defaultMessage: 'View plans',
  },
  // The CTA when the required tier is not on sale — "View plans" would send
  // the reader to a picker that answers "there are no self-serve plans".
  contactUs: {
    id: 'planUpgradeBanner.contactUs',
    defaultMessage: 'Contact us',
  },
  badge: {
    id: 'planUpgradeBanner.badge',
    defaultMessage: 'Plan upgrade',
  },
  tryFree: {
    id: 'planUpgradeBanner.tryFree',
    defaultMessage: 'Try free for {days} days',
  },
  starting: {
    id: 'planUpgradeBanner.starting',
    defaultMessage: 'Starting your trial…',
  },
  trialStarted: {
    id: 'planUpgradeBanner.trialStarted',
    defaultMessage: 'Your {days}-day free trial has started. Reloading…',
  },
  trialFailed: {
    id: 'planUpgradeBanner.trialFailed',
    defaultMessage: "Couldn't start the trial. Please try again or view plans.",
  },
});

const SUBSCRIPTION_ROUTE = '/admin/subscription';

// Plan-code -> display name (mirrors the subscription_plans catalog; CUSTOM
// is the Enterprise/contact-sales plan).
const PLAN_NAMES: Record<string, string> = {
  PAYG: 'Pay as you go',
  STARTER: 'Starter',
  TEAM: 'Team',
  GROWTH: 'Growth',
  SCALE: 'Scale',
  CUSTOM: 'Enterprise',
};

interface PlanUpgradeBannerProps {
  /** The structured 403 payload from `planUpgradeRequired(error)`. */
  gate: TPlanUpgradeRequired;
}

/**
 * Inline upsell for a per-plan feature gate (backend `plan_upgrade_required`
 * 403): "Upgrade to {Plan} to access this feature" + a link to the
 * Subscription page. Sibling of `FeatureUnavailableBanner` (same visual
 * family), but plan-aware and actionable.
 *
 * It owns both the sentence and the CTA, and callers must not pass copy of
 * their own — `gate.message` is deliberately not read here. Two call sites
 * used to pass it and neither ever rendered, so both carried a stale hardcoded
 * tier name ("Upgrade to Starter or higher") that looked authoritative in the
 * source and was invisible in the app. One component, one answer: that is also
 * what lets the tier-retired case below be fixed in a single place.
 *
 * When the backend says a trial is available (Phase 9) it also offers
 * "Try free for N days". This is the ONLY place that CTA lives — all eight
 * plan gates render this one component, so the funnel is wired everywhere at
 * once. `trialAvailable` is computed server-side against the account's real
 * eligibility, so the button is never shown where it would fail.
 */
export const PlanUpgradeBanner: React.FC<PlanUpgradeBannerProps> = ({ gate }) => {
  const intl = useIntl();
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const [starting, setStarting] = React.useState(false);
  const [started, setStarted] = React.useState(false);
  const [failed, setFailed] = React.useState(false);

  const planName = PLAN_NAMES[gate.requiredPlan] ?? gate.requiredPlan;
  // Two ways the required tier can be unbuyable, and they need the same
  // answer. `CUSTOM` never was self-serve. The rest become unbuyable when the
  // deployment retires them (`REACT_APP_HIDDEN_PLAN_CODES` — listing every
  // code is how self-serve is paused for a pricing review), and then "Upgrade
  // to Starter" names a tier that is not for sale and links to a picker with
  // nothing in it.
  //
  // Asked of the ONE required plan rather than of the whole catalog: it needs
  // no query, cannot flash the wrong copy while one loads, and stays right
  // when a single tier is retired instead of all of them.
  const cannotSelfServe =
    gate.requiredPlan === 'CUSTOM' || isPlanHidden(gate.requiredPlan);
  const trialDays = gate.trialDays ?? 14;
  const canTrial = Boolean(gate.trialAvailable && gate.addonCode) && !started;

  const onStartTrial = async () => {
    if (!gate.addonCode) return;
    setStarting(true);
    setFailed(false);
    try {
      const result = await startFeatureTrial(gate.addonCode, accessToken);
      if (isErrorResponseResult(result)) {
        setFailed(true);
        return;
      }
      setStarted(true);
      // The gate that rendered this banner failed server-side, so the page's
      // data is a 403 — invalidating won't help on its own. A reload is the
      // honest way to re-run every gated request on the page now that the
      // entitlement exists.
      await queryClient.invalidateQueries();
      window.setTimeout(() => window.location.reload(), 900);
    } catch {
      setFailed(true);
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className={styles.banner} role="status" data-test="plan-upgrade-banner">
      <span className={styles.badge}>
        <FormattedMessage {...messages.badge} />
      </span>
      <span className={styles.message}>
        {started ? (
          <FormattedMessage {...messages.trialStarted} values={{ days: trialDays }} />
        ) : failed ? (
          <FormattedMessage {...messages.trialFailed} />
        ) : cannotSelfServe ? (
          <FormattedMessage {...messages.contactSales} />
        ) : (
          <FormattedMessage {...messages.upgradeTo} values={{ plan: planName }} />
        )}
      </span>{' '}
      {canTrial && (
        <button
          type="button"
          className={styles.trialButton}
          onClick={onStartTrial}
          disabled={starting}
          data-test="plan-upgrade-try-free"
        >
          {starting
            ? intl.formatMessage(messages.starting)
            : intl.formatMessage(messages.tryFree, { days: trialDays })}
        </button>
      )}
      {cannotSelfServe ? (
        // `noopener` is the load-bearing half: without it the opened page gets
        // a handle on this one through `window.opener` and can navigate it
        // elsewhere.
        <a href={ENTERPRISE_CONTACT_URL} target="_blank" rel="noopener noreferrer">
          <FormattedMessage {...messages.contactUs} />
        </a>
      ) : (
        <Link to={SUBSCRIPTION_ROUTE}>
          <FormattedMessage {...messages.viewPlans} />
        </Link>
      )}
    </div>
  );
};

export default PlanUpgradeBanner;
